import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    CardHeader,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress
} from '@mui/material';
import {
    Email as EmailIcon,
    Send as SendIcon,
    Person as PersonIcon,
    History as HistoryIcon,
    Article as TemplateIcon,
    Preview as PreviewIcon,
    AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

/**
 * Page de communication email User ↔ Listings
 */
const UserListingsEmailPage = () => {
    const [searchParams] = useSearchParams();
    
    // États principaux
    const [activeTab, setActiveTab] = useState(0);
    const [emailData, setEmailData] = useState({
        userId: searchParams.get('userId') || '',
        listingIds: searchParams.get('listingIds')?.split(',') || [],
        subject: '',
        message: '',
        template: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [communicationHistory, setCommunicationHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Templates d'email prédéfinis
    const emailTemplates = [
        {
            id: 'validation_request',
            name: 'Demande de validation',
            subject: 'Validation requise pour votre annonce',
            content: `Bonjour,

Nous avons besoin de votre attention concernant votre annonce "{listing_title}".

Des informations supplémentaires sont nécessaires pour valider votre annonce. 

Veuillez vous connecter à votre compte pour apporter les modifications nécessaires.

Cordialement,
L'équipe Krillou`
        },
        {
            id: 'listing_approved',
            name: 'Annonce approuvée',
            subject: 'Votre annonce a été approuvée',
            content: `Félicitations !

Votre annonce "{listing_title}" a été approuvée et est maintenant visible publiquement.

Vous pouvez la consulter et la gérer depuis votre tableau de bord.

Cordialement,
L'équipe Krillou`
        },
        {
            id: 'listing_rejected',
            name: 'Annonce rejetée',
            subject: 'Votre annonce nécessite des modifications',
            content: `Bonjour,

Votre annonce "{listing_title}" nécessite des modifications avant d'être publiée.

Raison du rejet : {rejection_reason}

Veuillez modifier votre annonce et la soumettre à nouveau.

Cordialement,
L'équipe Krillou`
        },
        {
            id: 'general_notification',
            name: 'Notification générale',
            subject: 'Information importante',
            content: `Bonjour,

Nous vous contactons concernant vos annonces sur Krillou.

{custom_message}

N'hésitez pas à nous contacter si vous avez des questions.

Cordialement,
L'équipe Krillou`
        }
    ];

    /**
     * Charger l'historique des communications
     */
    const loadCommunicationHistory = async () => {
        if (!emailData.userId) return;

        try {
            setHistoryLoading(true);
            const response = await userListingService.getUserCommunicationHistory(
                emailData.userId,
                { page: page + 1, limit: rowsPerPage }
            );
            setCommunicationHistory(response.data || []);
            setTotalCount(response.pagination?.total || 0);
        } catch (err) {
            console.error('Erreur lors du chargement de l\'historique:', err);
        } finally {
            setHistoryLoading(false);
        }
    };

    /**
     * Charger l'historique au changement de tab ou de pagination
     */
    useEffect(() => {
        if (activeTab === 1) {
            loadCommunicationHistory();
        }
    }, [activeTab, emailData.userId, page, rowsPerPage]);

    /**
     * Appliquer un template
     */
    const handleApplyTemplate = (template) => {
        setEmailData(prev => ({
            ...prev,
            subject: template.subject,
            message: template.content,
            template: template.id
        }));
    };

    /**
     * Envoyer l'email
     */
    const handleSendEmail = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            if (!emailData.userId || !emailData.subject || !emailData.message) {
                setError('Veuillez remplir tous les champs obligatoires');
                return;
            }

            await userListingService.sendUserEmail(emailData);
            
            setSuccess('Email envoyé avec succès');
            
            // Réinitialiser le formulaire
            setEmailData(prev => ({
                ...prev,
                subject: '',
                message: '',
                template: ''
            }));

            // Recharger l'historique
            if (activeTab === 1) {
                loadCommunicationHistory();
            }
        } catch (err) {
            console.error('Erreur lors de l\'envoi de l\'email:', err);
            setError('Erreur lors de l\'envoi de l\'email');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Remplacer les variables dans le template
     */
    const replaceTemplateVariables = (content) => {
        return content
            .replace('{listing_title}', 'Titre de l\'annonce')
            .replace('{rejection_reason}', 'Raison du rejet')
            .replace('{custom_message}', emailData.message || 'Message personnalisé');
    };

    /**
     * Rendu de l'onglet composition d'email
     */
    const renderEmailComposer = () => (
        <Grid container spacing={3}>
            {/* Formulaire d'email */}
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Composer un email
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="ID Utilisateur destinataire"
                                value={emailData.userId}
                                onChange={(e) => setEmailData(prev => ({ ...prev, userId: e.target.value }))}
                                placeholder="uuid-de-l-utilisateur"
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="IDs des annonces concernées (optionnel)"
                                value={emailData.listingIds.join(', ')}
                                onChange={(e) => setEmailData(prev => ({ 
                                    ...prev, 
                                    listingIds: e.target.value.split(',').map(id => id.trim()).filter(Boolean)
                                }))}
                                placeholder="uuid1, uuid2, uuid3..."
                                helperText="Laissez vide si l'email ne concerne pas d'annonces spécifiques"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Sujet"
                                value={emailData.subject}
                                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                                placeholder="Sujet de votre email..."
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message"
                                multiline
                                rows={8}
                                value={emailData.message}
                                onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                                placeholder="Rédigez votre message..."
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setPreviewDialog(true)}
                                    startIcon={<PreviewIcon />}
                                    disabled={!emailData.subject || !emailData.message}
                                >
                                    Aperçu
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSendEmail}
                                    startIcon={<SendIcon />}
                                    disabled={loading || !emailData.userId || !emailData.subject || !emailData.message}
                                >
                                    {loading ? 'Envoi...' : 'Envoyer'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Templates */}
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <TemplateIcon sx={{ mr: 1 }} />
                        Templates
                    </Typography>

                    <List>
                        {emailTemplates.map((template, index) => (
                            <React.Fragment key={template.id}>
                                <ListItem
                                    button
                                    onClick={() => handleApplyTemplate(template)}
                                    selected={emailData.template === template.id}
                                >
                                    <ListItemText
                                        primary={template.name}
                                        secondary={template.subject}
                                    />
                                </ListItem>
                                {index < emailTemplates.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>

                {/* Résumé de l'envoi */}
                <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Résumé
                    </Typography>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Destinataire: {emailData.userId || 'Non spécifié'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Annonces concernées: {emailData.listingIds.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Template: {emailData.template || 'Aucun'}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );

    /**
     * Rendu de l'onglet historique
     */
    const renderHistory = () => (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ p: 2 }}>
                Historique des communications
            </Typography>

            {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Utilisateur</TableCell>
                                    <TableCell>Sujet</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Annonces</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {communicationHistory.map((comm) => (
                                    <TableRow key={comm.id} hover>
                                        <TableCell>
                                            {new Date(comm.sent_at).toLocaleDateString('fr-FR', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </TableCell>
                                        <TableCell>
                                            {comm.user_first_name} {comm.user_last_name}
                                        </TableCell>
                                        <TableCell>{comm.subject}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={comm.template || 'Personnalisé'} 
                                                size="small" 
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={comm.status} 
                                                size="small" 
                                                color={comm.status === 'sent' ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {comm.listing_count || 0} annonce(s)
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {communicationHistory.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                Aucun historique de communication
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage="Lignes par page"
                        labelDisplayedRows={({ from, to, count }) => 
                            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                        }
                    />
                </>
            )}
        </Paper>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PageHeader
                title="Communication Email User ↔ Listings"
                subtitle="Communiquez directement avec les utilisateurs via email"
                icon={<EmailIcon />}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            {/* Onglets */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Composer" icon={<EmailIcon />} />
                    <Tab label="Historique" icon={<HistoryIcon />} />
                </Tabs>
            </Paper>

            {/* Contenu des onglets */}
            {activeTab === 0 && renderEmailComposer()}
            {activeTab === 1 && renderHistory()}

            {/* Dialog de prévisualisation */}
            <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Aperçu de l'email</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            <strong>Sujet:</strong> {emailData.subject}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            <strong>Destinataire:</strong> {emailData.userId}
                        </Typography>
                        {emailData.listingIds.length > 0 && (
                            <Typography variant="subtitle1" gutterBottom>
                                <strong>Annonces concernées:</strong> {emailData.listingIds.length}
                            </Typography>
                        )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {replaceTemplateVariables(emailData.message)}
                        </Typography>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>
                        Fermer
                    </Button>
                    <Button variant="contained" onClick={() => {
                        setPreviewDialog(false);
                        handleSendEmail();
                    }}>
                        Envoyer
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UserListingsEmailPage;
